import { useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { z } from 'zod'
import { Building2, Mail, Phone, Hash, Globe, Users } from 'lucide-react'
import recruiterService, { RecruiterProfile } from '@/services/recruiterService'
import { toast } from '@/stores/toastStore'
import Card from '@/components/common/Card'
import Button from '@/components/common/Button'
import Input from '@/components/common/Input'
import Textarea from '@/components/common/Textarea'
import Select from '@/components/common/Select'
import DeleteAccountSection from '@/components/profile/settings/DeleteAccountSection'

const profileSchema = z.object({
  companyName: z.string().min(2, 'Company name is required'),
  companyWebsite: z.string().url('Invalid URL').optional().or(z.literal('')),
  companySize: z.string().optional(),
  industry: z.string().optional(),
  contactPerson: z.string().min(2, 'Contact person name is required'),
  phone: z.string().optional(),
  address: z.string().optional(),
  taxId: z.string().optional(),
})

type ProfileFormData = z.infer<typeof profileSchema>

export default function RecruiterProfileEdit() {
  const navigate = useNavigate()
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)
  const [, setProfile] = useState<RecruiterProfile | null>(null)

  const {
    register,
    handleSubmit,
    setValue,
    formState: { errors },
  } = useForm<ProfileFormData>({
    resolver: zodResolver(profileSchema),
  })

  useEffect(() => {
    loadProfile()
  }, [])

  const loadProfile = async () => {
    try {
      setLoading(true)
      const data = await recruiterService.getProfile()
      setProfile(data)

      // Set form values
      setValue('companyName', data.companyName)
      setValue('companyWebsite', data.companyWebsite || '')
      setValue('companySize', data.companySize || '')
      setValue('industry', data.industry || '')
      setValue('contactPerson', data.contactPerson)
      setValue('phone', data.phone || '')
      setValue('address', data.address || '')
      setValue('taxId', data.taxId || '')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to load profile')
    } finally {
      setLoading(false)
    }
  }

  const onSubmit = async (data: ProfileFormData) => {
    try {
      setSaving(true)
      await recruiterService.updateProfile({
        companyName: data.companyName,
        companyWebsite: data.companyWebsite || null,
        companySize: data.companySize || null,
        industry: data.industry || null,
        contactPerson: data.contactPerson,
        phone: data.phone || null,
        address: data.address || null,
        taxId: data.taxId || null,
      })
      toast.success('Profile updated successfully')
      navigate('/recruiter/dashboard')
    } catch (error: any) {
      toast.error(error.response?.data?.error || 'Failed to update profile')
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="container-custom py-12">
        <div className="flex items-center justify-center min-h-[400px]">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="container-custom py-8 max-w-4xl">
      {/* Header */}
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">Edit Recruiter Profile</h1>
        <p className="text-gray-600">Update your company information</p>
      </div>

      <form onSubmit={handleSubmit(onSubmit)} className="space-y-6">
        {/* Company Information */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Building2 className="w-5 h-5" />
            Company Information
          </h2>

          <div className="space-y-4">
            <Input
              {...register('companyName')}
              type="text"
              label="Company Name *"
              placeholder="Acme Corporation"
              error={errors.companyName?.message}
              icon={<Building2 className="w-5 h-5" />}
              disabled={saving}
            />

            <Input
              {...register('companyWebsite')}
              type="url"
              label="Company Website"
              placeholder="https://www.company.com"
              error={errors.companyWebsite?.message}
              icon={<Globe className="w-5 h-5" />}
              disabled={saving}
            />

            <div className="grid md:grid-cols-2 gap-4">
              <Select
                label="Company Size"
                {...register('companySize')}
                disabled={saving}
                options={[
                  { value: '1-10', label: '1-10 employees' },
                  { value: '11-50', label: '11-50 employees' },
                  { value: '51-200', label: '51-200 employees' },
                  { value: '201-500', label: '201-500 employees' },
                  { value: '501-1000', label: '501-1000 employees' },
                  { value: '1000+', label: '1000+ employees' },
                ]}
              />

              <Input
                {...register('industry')}
                type="text"
                label="Industry"
                placeholder="e.g., Technology, Healthcare"
                error={errors.industry?.message}
                icon={<Users className="w-5 h-5" />}
                disabled={saving}
              />
            </div>

            <div className="grid md:grid-cols-2 gap-4">
              <Input
                {...register('taxId')}
                type="text"
                label="Tax ID / Registration Number"
                placeholder="1234567890"
                error={errors.taxId?.message}
                icon={<Hash className="w-5 h-5" />}
                disabled={saving}
              />

              <Input
                {...register('phone')}
                type="tel"
                label="Phone Number"
                placeholder="+216 XX XXX XXX"
                error={errors.phone?.message}
                icon={<Phone className="w-5 h-5" />}
                disabled={saving}
              />
            </div>

            <Textarea
              {...register('address')}
              label="Company Address"
              placeholder="Enter full address..."
              rows={3}
              error={errors.address?.message}
              disabled={saving}
            />
          </div>
        </Card>

        {/* Contact Person */}
        <Card>
          <h2 className="text-xl font-semibold text-gray-900 mb-6 flex items-center gap-2">
            <Mail className="w-5 h-5" />
            Contact Person
          </h2>

          <div className="space-y-4">
            <Input
              {...register('contactPerson')}
              type="text"
              label="Contact Person Name *"
              placeholder="John Doe"
              error={errors.contactPerson?.message}
              icon={<Users className="w-5 h-5" />}
              disabled={saving}
            />

            <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
              <p className="text-sm text-blue-800">
                <strong>Note:</strong> This is the main point of contact for your company account.
                For security reasons, email changes must be done through account settings.
              </p>
            </div>
          </div>
        </Card>

        {/* Action Buttons */}
        <div className="flex items-center justify-end gap-4 pt-6 border-t border-gray-200">
          <Button
            type="button"
            variant="secondary"
            onClick={() => navigate('/recruiter/dashboard')}
            disabled={saving}
          >
            Cancel
          </Button>
          <Button type="submit" variant="primary" isLoading={saving}>
            Save Changes
          </Button>
        </div>
      </form>

      {/* Delete Account Section - Danger Zone */}
      <div className="mt-8">
        <DeleteAccountSection />
      </div>
    </div>
  )
}
